/* eslint-disable ssr-friendly/no-dom-globals-in-react-fc */
import * as React from 'react';

import SideNav from '../../SideNav/SideNav';
import EmptyState from '../../EmptyState/EmptyState';
import { Filters, FilterStateManager, FilterType } from '../types';
import { getFilterIDs } from '../hooks/useInternalFilterState';
import { useListKeyboardNavigation } from '../../../hooks/useListKeyboardNavigation';

import { FilterPanelWrapper, FilterMenu, FilterView } from '../styles';

export type FilterPanelProps<T extends FilterType> = {
  id: React.HTMLAttributes<HTMLElement>['id'];
  filters: Filters<T>;
  manager: FilterStateManager<T>;
  wrapperRef: React.RefObject<HTMLDialogElement>;
};

const FilterPanel = <T extends FilterType>({ manager, filters, ...props }: FilterPanelProps<T>) => {
  const group = React.useRef<HTMLUListElement>(null);
  const panel = React.useRef<HTMLDivElement>(null);

  const mounted = React.useRef(false);

  useListKeyboardNavigation({
    listElementRef: props.wrapperRef,
    childrenWrapperRef: group,
    close: manager.actions.close,
    isOpen: manager.state.open,
    closeOnBoundary: false,
    handleFocusEntry: false,
  });

  React.useLayoutEffect(() => {
    if (!group.current || mounted.current) {
      return;
    }
    const activeNavItem = group.current.querySelector<HTMLAnchorElement>('a[aria-current="true"]');

    if (activeNavItem) {
      activeNavItem.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
      group.current.focus();

      mounted.current = true;
    }
  });

  const filterIDs = getFilterIDs(filters);
  const activeTab = manager.state.activeTab;

  const title = filters?.[manager.state.activeTab]?.title || 'Unknown filter';
  const FilterPanelComponent = filters?.[manager.state.activeTab]?.component;

  // We have to do this to avoid FilterPanelComponent being re-created on every render,
  // which causes input fields inside the component to lose focus.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const StableFilterPanelComponent = React.useMemo(() => FilterPanelComponent, [activeTab]);

  return (
    <FilterPanelWrapper ref={props.wrapperRef} aria-label="Filter panel">
      <FilterMenu>
        <SideNav width="180px">
          <SideNav.ContentContainer>
            <SideNav.Groups ref={group} tabIndex={0} aria-label="Filters list" role="tablist">
              {filterIDs.map(filterId => (
                <SideNav.Group key={filterId.toString()}>
                  <SideNav.Item
                    noStripe
                    role="tab"
                    tabIndex={0}
                    aria-current={activeTab === filterId}
                    aria-label={`Filter by "${filters[filterId].title}"`}
                    title={String(filters[filterId]?.title || filterId)}
                    bubble={filters[filterId].bubble?.(manager.actions.getFilterValue(filterId))}
                    isActive={activeTab === filterId}
                    onClick={() => manager.actions.setActiveFilter(filterId)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        manager.actions.setActiveFilter(filterId);
                        panel.current?.focus();
                      }
                    }}
                  />
                </SideNav.Group>
              ))}
            </SideNav.Groups>
          </SideNav.ContentContainer>
        </SideNav>
      </FilterMenu>
      <FilterView ref={panel} aria-roledescription={`Configure filter by "${title}"`} tabIndex={0} role="tabpanel">
        {StableFilterPanelComponent ? (
          <StableFilterPanelComponent
            key={String(activeTab)}
            reset={() => manager.actions.resetFilter(activeTab)}
            setValue={manager.actions.makeSetFilterValue(activeTab)}
            value={manager.actions.getFilterValue(activeTab)}
          />
        ) : (
          <EmptyState type="block" icon="error" text="Missing filter component" />
        )}
      </FilterView>
    </FilterPanelWrapper>
  );
};

export default FilterPanel;
